import { Asset } from '@/firebase/assetService';
import { TaskWithDetails } from '@/services/taskService';
import { Checklist } from '@/firebase/checklistService';
import { Form } from '@/firebase/formService';
import { ChecklistCompletion } from '@/firebase/checklistCompletionService';
import { FormCompletion } from '@/firebase/formCompletionService';

/** Get timestamp in ms for comparison */
function getTimeMs(value: any): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (value?.toDate && typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value === 'number') return value;
  return new Date(value).getTime();
}

/**
 * Completion is for this asset. Matches Asset Record page: assetId, variableValues.assetId, variableValues.asset.
 * Also supports assetName/assetLocation and Room Number for robustness.
 */
function completionMatchesAsset(
  c: { assetId?: string; variableValues?: Record<string, string>; fieldValues?: Record<string, unknown> },
  asset: Asset
): boolean {
  const aid = c.assetId || c.variableValues?.['assetId'] || c.variableValues?.['asset'] ||
    c.variableValues?.['Room Number'] || c.variableValues?.['Room'] ||
    (c as any).fieldValues?.['assetId'] || (c as any).fieldValues?.['asset'];
  if (!aid) return false;
  return (
    aid === asset.id ||
    aid === asset.assetName ||
    (!!asset.assetLocation && aid === asset.assetLocation)
  );
}

/**
 * Compute whether an asset has pending work.
 * Logic matches Asset Record page open task count:
 * - Red = open task count > 0 (tasks that need action, no checklist evidence)
 * - Green = open task count = 0 (all tasks have checklist evidence or no tasks apply)
 *
 * Only checklist completion counts as evidence (not form). Evidence must be completed on or after task start.
 */
export function assetHasPendingWork(params: {
  asset: Asset;
  applyingTasks: TaskWithDetails[];
  assetTypeTemplateIds: string[];
  checklists: Checklist[];
  forms: Form[];
  checklistCompletions: ChecklistCompletion[];
  formCompletions: FormCompletion[];
}): boolean {
  const {
    asset,
    applyingTasks,
    assetTypeTemplateIds,
    checklists,
    checklistCompletions,
  } = params;

  const assetId = asset.id;
  if (!assetId) return false;

  const tasksForAsset = applyingTasks.filter(
    (t) => t.locationId === asset.locationId && assetTypeTemplateIds.includes(t.templateId)
  );
  if (tasksForAsset.length === 0) return false;

  const checklistTemplateMap = new Map<string, string>();
  checklists.forEach((c) => {
    if (c.id && c.templateId) checklistTemplateMap.set(c.id, c.templateId);
  });

  for (const task of tasksForAsset) {
    const taskStartMs = getTimeMs(task.createdAt);

    // Checklist completion for this asset, same template, completed on or after task start = evidence (matches Asset Record)
    const hasChecklistEvidence = checklistCompletions.some((cc) => {
      if (!completionMatchesAsset(cc, asset)) return false;
      const checklistTemplateId = cc.checklistId ? checklistTemplateMap.get(cc.checklistId) : undefined;
      if (checklistTemplateId !== task.templateId) return false;
      const completionMs = getTimeMs(cc.completedAt ?? (cc as any).completedDate);
      return completionMs >= taskStartMs;
    });

    // If checklist evidence satisfies this task, no pending work for this task
    if (hasChecklistEvidence) continue;

    // Required evidence: checklist can satisfy it. If not satisfied, pending.
    const hasRequiredEvidence = (task.evidenceTypes ?? []).some(
      (e: { required?: boolean }) => e.required === true || e.required === undefined
    );
    if (hasRequiredEvidence) return true;

    // No required evidence, but has checklists that need to be run
    const taskChecklists = checklists.filter((c) => c.templateId === task.templateId);
    if (taskChecklists.length > 0) return true;
  }

  return false;
}
